import { AuthSdkError } from "./errors";
import { generateCodeVerifier, generateCodeChallenge } from "./pkce";
import { createTokenVerifier } from "./verify";

import type {
  AuthClient,
  AuthClientConfig,
  AuthTokens,
  AuthUser,
  HandleCallbackOptions,
  LoginOptions,
  OAuthProvider,
} from "./types";

export function createAuthClient(config: AuthClientConfig): AuthClient {
  const { authUrl, clientId, redirectUri, storage } = config;
  let verifier: ((token: string) => Promise<import("./types").AuthTokenPayload>) | null = null;

  function getVerifier() {
    if (!verifier) {
      verifier = createTokenVerifier(authUrl);
    }
    return verifier;
  }

  async function postForm(
    url: string,
    params: Record<string, string>,
    options?: { credentials?: "include" | "omit" | "same-origin" },
  ): Promise<Response> {
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams(params),
      credentials: options?.credentials,
    });
  }

  async function handleTokenResponse(response: Response): Promise<AuthTokens> {
    if (!response.ok) {
      throw await AuthSdkError.fromResponse(response);
    }

    const data = (await response.json()) as AuthTokens;
    await storage.setTokens(data);
    return data;
  }

  return {
    async login(options: LoginOptions): Promise<string> {
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      await options.pkceStorage.set(codeVerifier);

      const params = new URLSearchParams({
        client_id: clientId,
        redirect_uri: redirectUri,
        code_challenge: codeChallenge,
        code_challenge_method: "S256",
      });

      if (options.provider) {
        params.set("provider", options.provider);
      }
      if (options.state) {
        params.set("state", options.state);
      }

      return `${authUrl}/authorize?${params.toString()}`;
    },

    async handleCallback(options: HandleCallbackOptions): Promise<AuthTokens> {
      const codeVerifier = await options.pkceStorage.get();
      if (!codeVerifier) {
        throw new AuthSdkError("pkce_missing", "No PKCE code verifier found in storage");
      }

      const response = await postForm(
        `${authUrl}/token`,
        {
          grant_type: "authorization_code",
          code: options.code,
          code_verifier: codeVerifier,
          client_id: clientId,
          redirect_uri: redirectUri,
        },
        { credentials: "include" },
      );

      const tokens = await handleTokenResponse(response);
      await options.pkceStorage.clear();
      return tokens;
    },

    async loginWithIdentityToken(
      provider: OAuthProvider,
      identityToken: string,
    ): Promise<AuthTokens> {
      const response = await postForm(
        `${authUrl}/token`,
        {
          grant_type: "identity_token",
          provider,
          identity_token: identityToken,
          client_id: clientId,
        },
        { credentials: "include" },
      );

      return handleTokenResponse(response);
    },

    async refresh(): Promise<AuthTokens> {
      const tokens = await storage.getTokens();

      // Build params — always include client_id
      const params: Record<string, string> = { client_id: clientId };

      // If we have a refresh token in storage (migration flow or mobile),
      // include it in the body
      if (tokens?.refreshToken) {
        params.refresh_token = tokens.refreshToken;
      }

      const response = await postForm(`${authUrl}/token/refresh`, params, {
        credentials: "include",
      });

      if (!response.ok) {
        throw await AuthSdkError.fromResponse(response);
      }

      const data = (await response.json()) as AuthTokens;
      await storage.setTokens(data);
      return data;
    },

    async logout(): Promise<void> {
      const tokens = await storage.getTokens();
      await storage.clearTokens();

      // Build revoke params — include body refresh token if available (migration/mobile)
      const params: Record<string, string> = {};
      if (tokens?.refreshToken) {
        params.refresh_token = tokens.refreshToken;
      }

      // Fire-and-forget — don't throw if revoke fails
      // credentials: 'include' sends the httpOnly cookie for server-side deletion
      postForm(`${authUrl}/token/revoke`, params, { credentials: "include" }).catch(() => {});
    },

    async deleteAccount(): Promise<void> {
      const tokens = await storage.getTokens();
      if (!tokens?.accessToken) {
        throw new AuthSdkError("token_expired", "No access token available");
      }

      const response = await fetch(`${authUrl}/me`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${tokens.accessToken}` },
      });

      if (!response.ok) {
        throw await AuthSdkError.fromResponse(response);
      }

      await storage.clearTokens();
    },

    async verifyToken(token: string) {
      return getVerifier()(token);
    },

    async getUser(): Promise<AuthUser> {
      const tokens = await storage.getTokens();
      if (!tokens?.accessToken) {
        throw new AuthSdkError("token_expired", "No access token available");
      }

      const response = await fetch(`${authUrl}/userinfo`, {
        headers: { Authorization: `Bearer ${tokens.accessToken}` },
      });

      if (!response.ok) {
        throw await AuthSdkError.fromResponse(response);
      }

      return (await response.json()) as AuthUser;
    },

    async getTokens() {
      return storage.getTokens();
    },
  };
}
